/**
 * @fileoverview Componente responsável por iniciar novas conversas manualmente.
 * Visual dos diálogos do app (ConfirmationModal/TriageDialog): título com X,
 * rótulo pequeno em cima e valor embaixo, rodapé Cancelar + ação principal.
 */

import React, { useState, useEffect, useContext, useMemo } from "react";
import Dialog from "@material-ui/core/Dialog";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { X, Copy, Check, MessageCircle, Wifi, WifiOff, QrCode, KeyRound, Loader } from "lucide-react";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import ModernSelect from "../ModernSelect";
import { i18n } from "../../translate/i18n";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
import { useCopyFeedback } from "../../hooks/useCopyFeedback";
import { getConnectionStatus, STATUS_TEXT_ON_LIGHT } from "../../utils/connectionStatus";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const textPrimary = dark ? "rgba(255,255,255,0.92)" : "#0f172a";
  const textSecondary = dark ? "rgba(255,255,255,0.64)" : "#475569";
  const textMuted = dark ? "rgba(255,255,255,0.45)" : "#64748b";
  const hover = dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.045)";
  const inputBorder = dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.18)";
  const t = "all 160ms cubic-bezier(0.4, 0, 0.2, 1)";
  const btn = {
    height: 38,
    padding: "0 18px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: t,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    "&:disabled": { opacity: 0.55, cursor: "not-allowed", boxShadow: "none" },
  };

  return {
    paper: {
      borderRadius: 18,
      background: dark ? "#1c1e23" : "#ffffff",
      width: "100%",
      maxWidth: 420,
      margin: 16,
      boxShadow: dark
        ? "0 24px 48px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)"
        : "0 24px 48px -12px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.04)",
    },
    header: { display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 20px 0" },
    headText: { flex: 1, minWidth: 0 },
    title: { fontSize: 17, fontWeight: 600, color: textPrimary, lineHeight: 1.35, margin: 0 },
    subtitle: { fontSize: 13.5, color: textSecondary, lineHeight: 1.5, margin: "2px 0 0" },
    iconBtn: {
      background: "transparent",
      border: "none",
      padding: 6,
      cursor: "pointer",
      color: textMuted,
      display: "inline-flex",
      borderRadius: 8,
      transition: t,
      "&:hover:not(:disabled)": { color: textPrimary, background: hover },
      "&:disabled": { opacity: 0.4, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${textMuted}`, outlineOffset: 1 },
      "& svg": { width: 18, height: 18 },
    },
    close: { margin: "-6px -6px 0 0" },
    body: { padding: "20px 20px 8px", display: "flex", flexDirection: "column", gap: 18 },
    label: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: textMuted,
      marginBottom: 6,
    },
    numberRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
    number: {
      fontSize: 22,
      fontWeight: 600,
      color: textPrimary,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "0.01em",
      wordBreak: "break-all",
    },
    copied: { color: dark ? "#00a884" : "#008069", "&:hover:not(:disabled)": { color: dark ? "#00a884" : "#008069" } },
    // Conexão: ícone do status, nome e número (no menu e na conexão única); no
    // campo do select, tudo numa linha.
    opt: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    optCompact: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    optIcon: { flexShrink: 0 },
    optText: { display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.3 },
    optTextCompact: { display: "flex", alignItems: "baseline", gap: 6, minWidth: 0, overflow: "hidden" },
    optName: { fontSize: 14.5, fontWeight: 500, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    optMeta: { fontSize: 12.5, color: textMuted, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    placeholder: { color: textMuted },
    invalid: { marginTop: 6 },
    empty: { fontSize: 13.5, color: dark ? "#fca5a5" : "#dc2626", lineHeight: 1.5, margin: 0 },
    select: { "& .MuiInputBase-root": { fontSize: 14 } },
    footer: { display: "flex", justifyContent: "flex-end", gap: 8, padding: "16px 20px 20px" },
    btnGhost: {
      ...btn,
      border: `1px solid ${inputBorder}`,
      background: "transparent",
      color: textSecondary,
      "&:hover:not(:disabled)": { background: hover, color: textPrimary },
    },
    btnPrimary: {
      ...btn,
      minWidth: 128,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      boxShadow: dark ? "0 6px 14px -4px rgba(37,99,235,0.45)" : "0 6px 14px -4px rgba(59,130,246,0.45)",
      "&:hover:not(:disabled)": { background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)" },
      "&:focus-visible": { outline: "2px solid #93b4ff", outlineOffset: 2 },
      "& svg": { width: 17, height: 17 },
    },
  };
});

// DDDs onde o WhatsApp NÃO usa o 9º dígito (mesmo que a ANATEL exija)
// Fonte: backend/src/services/WbotServices/CheckNumber.ts
const DDD_WITHOUT_NINE = [
  // Minas Gerais
  "31", "32", "33", "34", "35", "37", "38",
  // Paraná
  "41", "42", "43", "44", "45", "46",
  // Santa Catarina
  "47", "48", "49",
  // Rio Grande do Sul
  "51", "53", "54", "55",
  // Centro-Oeste
  "61", "62", "63", "64", "65", "66", "67",
  // Norte
  "68", "69", "91", "92", "93", "94", "95", "96", "97",
  // Nordeste
  "71", "73", "74", "75", "77", "79", "81", "82", "83", "84", "85", "86", "87", "88", "89", "98", "99"
];

// Só dígitos. Com "+", o código do país já veio no número; sem ele, tira o 0 da
// frente e número de até 11 dígitos é nacional (ganha o 55).
const withCountryCode = (phone) => {
  const input = String(phone || "").trim();
  let cleaned = input.replace(/\D/g, "");
  if (input.startsWith("+")) return cleaned;
  if (cleaned.startsWith("0")) cleaned = cleaned.substring(1);
  if (cleaned && cleaned.length <= 11) cleaned = "55" + cleaned;
  return cleaned;
};

const formatPhoneForApi = (phone) => {
  let cleaned = withCountryCode(phone);

  // Remove o 9º dígito para DDDs que o WhatsApp não usa
  // Ex: 5551999999999 (13 dígitos) → 555199999999 (12 dígitos)
  if (cleaned.length === 13 && cleaned.startsWith("55")) {
    const ddd = cleaned.substring(2, 4);
    const firstDigitAfterDDD = cleaned.charAt(4);

    if (firstDigitAfterDDD === "9" && DDD_WITHOUT_NINE.includes(ddd)) {
      cleaned = cleaned.substring(0, 4) + cleaned.substring(5);
    }
  }

  return cleaned;
};

const STATUS_ICON = {
  CONNECTED: Wifi,
  WORKING: Wifi,
  DISCONNECTED: WifiOff,
  TIMEOUT: WifiOff,
  FAILED: WifiOff,
  STOPPED: WifiOff,
  QRCODE: QrCode,
  SCAN_QR_CODE: QrCode,
  PASSKEY_REQUIRED: KeyRound,
  PASSKEY_CONFIRMATION_REQUIRED: KeyRound,
};

// Conexão com o status em ícone (cor e rótulo da tela de Conexões) e o número
// formatado. Fora do ar, o rótulo do status vai junto do número.
const ConnectionOption = ({ whatsapp, compact = false }) => {
  const classes = useStyles();
  const dark = useTheme().palette.type === "dark";
  const info = getConnectionStatus(whatsapp.status, whatsapp.provider);
  const Icon = STATUS_ICON[(whatsapp.status || "").toUpperCase()] || Loader;
  const iconColor = dark ? info.color : STATUS_TEXT_ON_LIGHT[info.color] || info.color;
  const displayNumber = whatsapp.number ? formatPhoneNumber(whatsapp.number) : "";
  const meta = [displayNumber, whatsapp.status === "CONNECTED" ? "" : info.label].filter(Boolean).join(" · ");

  return (
    <span className={compact ? classes.optCompact : classes.opt}>
      <Icon size={16} strokeWidth={2} color={iconColor} className={classes.optIcon} role="img" aria-label={info.label} />
      <span className={compact ? classes.optTextCompact : classes.optText}>
        <span className={classes.optName}>{whatsapp.name}</span>
        {meta && <span className={classes.optMeta}>{meta}</span>}
      </span>
    </span>
  );
};

// contactName: nome do contato a criar (ex.: cartão de contato recebido); sem ele, o número.
const StartConversationModal = ({ open, onClose, phoneNumber, contactName }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);
  const { copied, copy: handleCopyNumber } = useCopyFeedback(phoneNumber, {
    copiedMessage: i18n.t("startConversationModal.copied"),
    failedMessage: i18n.t("startConversationModal.copyFailed"),
  });

  // Número que vai para a API e o que aparece na tela: brasileiro formatado;
  // estrangeiro ou fora do padrão, como veio. Brasileiro precisa de DDD + 8 ou 9
  // dígitos; o de outro país (veio com "+"), de 8 a 15. Fora disso (ou texto, como
  // "não informado") não é telefone: a busca de contato acharia qualquer um.
  const apiNumber = formatPhoneForApi(phoneNumber);
  const isValidNumber = apiNumber.startsWith("55") ? /^55\d{10,11}$/.test(apiNumber) : /^\d{8,15}$/.test(apiNumber);
  const withCountryCodeNumber = withCountryCode(phoneNumber);
  const displayedNumber = formatPhoneNumber(withCountryCodeNumber) !== withCountryCodeNumber ? formatPhoneNumber(withCountryCodeNumber) : String(phoneNumber || "").trim();

  // Conexões que iniciam conversa por telefone (LiveChat não: fica sempre
  // CONNECTED, mas não tem número). Conectadas primeiro; as outras aparecem no
  // menu desabilitadas, com o status.
  const connections = useMemo(() => {
    const phoneConnections = whatsApps.filter((w) => w.provider !== "livechat");
    const connected = phoneConnections.filter((w) => w.status === "CONNECTED");
    return [...connected, ...phoneConnections.filter((w) => w.status !== "CONNECTED")];
  }, [whatsApps]);
  const connectedWhatsapps = useMemo(() => connections.filter((w) => w.status === "CONNECTED"), [connections]);
  // A escolhida pode cair com o modal aberto: fica marcada, mas não inicia.
  const isSelectedConnected = connectedWhatsapps.some((w) => w.id === selectedWhatsapp);

  useEffect(() => {
    // Com uma única conexão no ar, já vem escolhida; nunca troca uma escolha feita.
    if (open && connectedWhatsapps.length === 1) {
      setSelectedWhatsapp((current) => current || connectedWhatsapps[0].id);
    }
  }, [open, connectedWhatsapps]);

  const handleClose = () => {
    setSelectedWhatsapp("");
    setLoading(false);
    onClose();
  };

  const handleStartConversation = async () => {
    if (!isValidNumber) {
      toast.error(i18n.t("startConversationModal.invalidNumber"));
      return;
    }
    if (!isSelectedConnected) {
      toast.error(i18n.t("startConversationModal.selectRequired"));
      return;
    }

    setLoading(true);

    try {
      const formattedNumber = apiNumber;
      // Estrangeiro (veio com "+"): o backend mantém o código do país só com o "+".
      const contactNumber =
        String(phoneNumber || "").trim().startsWith("+") && !apiNumber.startsWith("55")
          ? `+${apiNumber}`
          : apiNumber;

      // Primeiro, verificar se o contato já existe ou criar
      let contactId;

      try {
        // Tentar buscar contato existente
        const { data: contacts } = await api.get("/contacts", {
          params: { searchParam: formattedNumber }
        });

        const existingContact = contacts.contacts.find(
          c => c.number === formattedNumber || c.number?.includes(formattedNumber.slice(-9))
        );

        if (existingContact) {
          contactId = existingContact.id;
        } else {
          // Criar novo contato
          const { data: newContact } = await api.post("/contacts", {
            name: contactName?.trim() || formattedNumber,
            number: contactNumber,
          });
          contactId = newContact.id;
        }
      } catch (err) {
        // Se falhar ao buscar, criar novo
        const { data: newContact } = await api.post("/contacts", {
          name: contactName?.trim() || formattedNumber,
          number: contactNumber,
        });
        contactId = newContact.id;
      }

      // Criar ticket sem fila (queueId: null)
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        queueId: null,
        whatsappId: selectedWhatsapp,
        userId: user.id,
        status: "open",
      });

      handleClose();
      history.push(`/tickets/${ticket.uuid}`);
      toast.success(i18n.t("startConversationModal.started"));
    } catch (err) {
      console.error("Erro ao iniciar conversa:", err);
      toastError(err);
    }

    setLoading(false);
  };

  const noConnection = connectedWhatsapps.length === 0;
  const copyLabel = i18n.t(copied ? "startConversationModal.copied" : "startConversationModal.copy");

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      classes={{ paper: classes.paper }}
      disableEscapeKeyDown={loading}
      aria-labelledby="start-conversation-title"
    >
      <div className={classes.header}>
        <div className={classes.headText}>
          <h2 id="start-conversation-title" className={classes.title}>
            {i18n.t("startConversationModal.title")}
          </h2>
          <p className={classes.subtitle}>{i18n.t("startConversationModal.subtitle")}</p>
        </div>
        <button
          type="button"
          className={`${classes.iconBtn} ${classes.close}`}
          onClick={handleClose}
          disabled={loading}
          aria-label={i18n.t("startConversationModal.cancel")}
        >
          <X />
        </button>
      </div>

      <div className={classes.body}>
        <div>
          <div className={classes.label}>{i18n.t("startConversationModal.number")}</div>
          <div className={classes.numberRow}>
            <span className={classes.number}>{displayedNumber}</span>
            <Tooltip title={copyLabel} placement="top" arrow>
              <button
                type="button"
                className={`${classes.iconBtn} ${copied ? classes.copied : ""}`}
                onClick={handleCopyNumber}
                aria-label={copyLabel}
              >
                {copied ? <Check /> : <Copy />}
              </button>
            </Tooltip>
          </div>
          {!isValidNumber && <p className={`${classes.empty} ${classes.invalid}`}>{i18n.t("startConversationModal.invalidNumber")}</p>}
        </div>

        <div>
          <div className={classes.label}>
            {i18n.t("startConversationModal.connection")}
          </div>
          {noConnection ? (
            <p className={classes.empty}>{i18n.t("startConversationModal.noConnection")}</p>
          ) : connections.length > 1 ? (
            <ModernSelect
              className={classes.select}
              size="small"
              fullWidth
              value={selectedWhatsapp}
              onChange={(e) => setSelectedWhatsapp(e.target.value)}
              displayEmpty
              renderValue={(id) => {
                const selected = connections.find((w) => w.id === id);
                return selected ? (
                  <ConnectionOption whatsapp={selected} compact />
                ) : (
                  <span className={classes.placeholder}>{i18n.t("startConversationModal.selectConnection")}</span>
                );
              }}
              disabled={loading}
              inputProps={{ "aria-label": i18n.t("startConversationModal.connection") }}
            >
              {connections.map((whatsapp) => (
                <MenuItem key={whatsapp.id} value={whatsapp.id} disabled={whatsapp.status !== "CONNECTED"}>
                  <ConnectionOption whatsapp={whatsapp} />
                </MenuItem>
              ))}
            </ModernSelect>
          ) : (
            <ConnectionOption whatsapp={connectedWhatsapps[0]} />
          )}
        </div>
      </div>

      <div className={classes.footer}>
        <button type="button" className={classes.btnGhost} onClick={handleClose} disabled={loading}>
          {i18n.t("startConversationModal.cancel")}
        </button>
        <button
          type="button"
          className={classes.btnPrimary}
          onClick={handleStartConversation}
          disabled={loading || !isSelectedConnected || !isValidNumber}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : <MessageCircle />}
          {i18n.t(loading ? "startConversationModal.starting" : "startConversationModal.start")}
        </button>
      </div>
    </Dialog>
  );
};

export default StartConversationModal;
